import { createSlice } from '@reduxjs/toolkit';
import {
  userLoginAction,
  userClientAddAction,
  setCurrentClientId,
  setCurrentClientIndex,
  userEmailAction,
  userRoleAction,
  setCurrentViewAction,
  setIsCmpAccessAction,
  setUserTimezone,
  setCmpNameAction,
  setTrailPeriodAction,
  setUserTenantTypeAction,
  refreshCustomerListAction,
} from './login.actions';
import { InitialStateProps } from './types';

const initialState: InitialStateProps = {
  userLogin: '',
  isLoadingUsersLogin: false,
  currentView: 'contacts',
  clientKeyData: [{ CustomerKey: '', CustomerName: '', key: '' }],
  currentSelectedIndex: '0',
  clientUniqueId: '',
  userEmail: '',
  userRole: '',
  cmpAccess: '',
  cmpName: '',
  isLinkedInConfigured: '',
  userTimezone: '',
  trialPeriod: 0,
  userTenantType: '',
};

const userLogin = createSlice({
  name: 'login',
  initialState,
  reducers: {
    resetLoginState: () => initialState,
    setUserLogin: (state, action) => {
      if (action.payload.status === 'success') {
        state.isLoadingUsersLogin = true;
        state.userLogin = action.payload.obj.accessToken;
        state.clientKeyData = action.payload.obj.customer;
      } else {
        state.isLoadingUsersLogin = false;
        state.userLogin = '';
      }
    },
    setClientData: (state, action) => {
      if (action.payload.status === 'success') {
        state.clientKeyData = action.payload.obj.customer;
      } else {
        state.clientKeyData = [{ CustomerKey: '', CustomerName: '', key: '' }];
      }
    },
    setClientId: (state, action) => {
      if (action.payload.status === 'success') {
        state.clientUniqueId = action.payload.obj.customer[0];
      } else {
        state.clientKeyData = [{ CustomerKey: '', CustomerName: '', key: '' }];
      }
    },
    setClientView: (state, action) => {
      if (action.payload.status === 'success') {
        state.currentView = action.payload.obj.currentView;
      } else {
        state.currentView = 'contacts';
      }
    },
    setCmpAccess: (state, action) => {
      if (action.payload.status === 'success') {
        state.cmpAccess = action.payload.obj.cmpAccess;
      } else {
        state.cmpAccess = 'false';
      }
    },
    setLinkedInConfigured: (state, action) => {
      state.isLinkedInConfigured = action.payload;
    },
    setUserTimezone: (state, action) => {
      if (action.payload.status === 'success') {
        state.userTimezone = action.payload.obj.userTimezone;
      } else {
        state.userTimezone = '';
      }
    },
    setCmpName: (state, action) => {
      if (action.payload.status === 'success') {
        state.cmpName = action.payload.obj.cmpName;
      } else {
        state.cmpName = '';
      }
    },
    setTrailPeriod: (state, action) => {
      if (action.payload.status === 'success') {
        state.trialPeriod = action.payload.obj.trialPeriod;
      } else {
        state.trialPeriod = 0;
      }
    },
    setUserTenantType: (state, action) => {
      if (action.payload.status === 'success') {
        state.userTenantType = action.payload.obj.userTenantType;
      } else {
        state.userTenantType = '';
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(userLoginAction.pending, (state) => {
      state.isLoadingUsersLogin = false;
    });
    builder.addCase(userLoginAction.fulfilled, (state, action) => {
      state.isLoadingUsersLogin = true;
      state.userLogin = action.payload;
    });
    builder.addCase(userClientAddAction.fulfilled, (state, action) => {
      state.clientKeyData = action.payload;
      if (state.clientUniqueId) {
        const matchedIndex = action.payload.findIndex(
          (client) => client.CustomerKey === state.clientUniqueId,
        );
        if (matchedIndex >= 0) {
          state.currentSelectedIndex = matchedIndex.toString();
        }
      }
    });
    builder.addCase(userEmailAction.fulfilled, (state, action) => {
      state.userEmail = action.payload;
    });
    builder.addCase(userRoleAction.fulfilled, (state, action) => {
      state.userRole = action.payload;
    });
    builder.addCase(setCurrentClientId.fulfilled, (state, action) => {
      state.clientUniqueId = action.payload;
    });
    builder.addCase(setCurrentClientIndex.fulfilled, (state, action) => {
      state.currentSelectedIndex = action.payload.toString();
    });
    builder.addCase(setCurrentViewAction.fulfilled, (state, action) => {
      state.currentView = action.payload;
    });
    builder.addCase(userLoginAction.rejected, (state) => {
      state.isLoadingUsersLogin = false;
    });
    builder.addCase(setIsCmpAccessAction.fulfilled, (state, action) => {
      state.cmpAccess = action.payload;
    });
    builder.addCase(setIsCmpAccessAction.rejected, (state) => {
      state.cmpAccess = 'false';
    });
    builder.addCase(setUserTimezone.fulfilled, (state, action) => {
      state.userTimezone = action.payload;
    });
    builder.addCase(setUserTimezone.rejected, (state) => {
      state.userTimezone = '';
    });
    builder.addCase(setCmpNameAction.fulfilled, (state, action) => {
      state.cmpName = action.payload;
    });
    builder.addCase(setCmpNameAction.rejected, (state) => {
      state.cmpName = '';
    });
    builder.addCase(setTrailPeriodAction.fulfilled, (state, action) => {
      state.trialPeriod = action.payload;
    });
    builder.addCase(setTrailPeriodAction.rejected, (state) => {
      state.trialPeriod = 0;
    });
    builder.addCase(setUserTenantTypeAction.fulfilled, (state, action) => {
      state.userTenantType = action.payload;
    });
    builder.addCase(setUserTenantTypeAction.rejected, (state) => {
      state.userTenantType = '';
    });
    builder.addCase(refreshCustomerListAction.fulfilled, (state, action) => {
      if (action.payload && action.payload.length > 0) {
        state.clientKeyData = action.payload;
        if (state.clientUniqueId) {
          const matchedIndex = action.payload.findIndex(
            (client) => client.CustomerKey === state.clientUniqueId,
          );
          if (matchedIndex >= 0) {
            state.currentSelectedIndex = matchedIndex.toString();
          }
        }
      }
    });
  },
});

export const { setUserLogin, setLinkedInConfigured, resetLoginState } = userLogin.actions;

export default userLogin.reducer;
